import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ToolShell } from "@/components/tools/ToolShell";
import { NamePicker } from "@/components/tools/impl/NamePicker";
import { SpinWheel } from "@/components/tools/impl/SpinWheel";
import { TeamGenerator } from "@/components/tools/impl/TeamGenerator";
import {
  ClassroomTimer,
  CoinFlip,
  DiceRoller,
  NumberGenerator,
  SeatingChart,
} from "@/components/tools/impl/SimpleTools";
import {
  CardPicker,
  ColorPicker,
  LetterPicker,
  LineOrder,
  Scoreboard,
  YesNoPicker,
} from "@/components/tools/impl/PickerTools";
import {
  ACTIVITIES,
  BRAIN_BREAKS,
  CHALLENGES,
  CHARADES,
  CHOICES,
  CLASSROOM_JOBS,
  PICTIONARY,
  QUESTIONS,
  TRIVIA,
  WORDS,
  WOULD_YOU_RATHER,
} from "@/lib/presets";
import { getTool } from "@/lib/tools-catalog";
import { HELPER_ROLES, TOOL_CONTENT } from "@/lib/tool-content";

export const Route = createFileRoute("/free-tools/$slug")({
  loader: ({ params }) => {
    const tool = getTool(params.slug);
    if (!tool) throw notFound();
    throw redirect({ to: "/$slug", params: { slug: tool.slug }, statusCode: 301 });
  },
  head: ({ params }) => {
    const content = getToolContent(params.slug, params.slug);
    const canonicalUrl = `https://picamini.online/${params.slug}`;
    return {
      meta: [
        { title: content.seoTitle },
        { name: "description", content: content.seoDescription },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: content.seoTitle },
        { property: "og:description", content: content.seoDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonicalUrl },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: content.seoTitle },
        { name: "twitter:description", content: content.seoDescription },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
      ],
    };
  },
  component: ToolPage,
});

export function ToolBody({ slug }: { slug: string }) {
  switch (slug) {
    case "random-name-picker":
      return <NamePicker storageKey="pickamini:names" />;
    case "random-student-picker":
      return <NamePicker storageKey="pickamini:students" label="Class roster" />;
    case "classroom-helper-picker":
      return <NamePicker storageKey="pickamini:helpers" label="Student names" roles={HELPER_ROLES} />;
    case "spin-the-wheel":
      return <SpinWheel />;
    case "team-generator":
      return <TeamGenerator storageKey="pickamini:teams" unitLabel="Team" />;
    case "group-maker":
      return <TeamGenerator storageKey="pickamini:groups" unitLabel="Group" />;
    case "partner-generator":
      return <TeamGenerator storageKey="pickamini:partners" unitLabel="Pair" pairMode />;
    case "random-number-generator":
      return <NumberGenerator />;
    case "dice-roller":
      return <DiceRoller />;
    case "coin-flip":
      return <CoinFlip />;
    case "classroom-timer":
      return <ClassroomTimer />;
    case "seating-chart":
      return <SeatingChart />;
    case "random-letter-picker":
      return <LetterPicker />;
    case "yes-or-no-picker":
      return <YesNoPicker />;
    case "random-color-picker":
      return <ColorPicker />;
    case "random-line-order":
      return <LineOrder />;
    case "classroom-scoreboard":
      return <Scoreboard />;
    case "classroom-job-picker":
      return <NamePicker storageKey="pickamini:jobs" label="Student names" roles={CLASSROOM_JOBS} />;
    case "random-word-picker":
      return (
        <CardPicker
          storageKey="pickamini:words"
          presets={WORDS}
          actionLabel="Pick a word"
          entriesLabel="Words"
        />
      );
    case "random-choice-picker":
      return (
        <CardPicker
          storageKey="pickamini:choices"
          presets={CHOICES}
          actionLabel="Choose"
          entriesLabel="Choices"
        />
      );
    case "random-question-picker":
      return (
        <CardPicker
          storageKey="pickamini:questions"
          presets={QUESTIONS}
          actionLabel="Pick a question"
          entriesLabel="Questions"
        />
      );
    case "activity-picker":
      return (
        <CardPicker
          storageKey="pickamini:activities"
          presets={ACTIVITIES}
          actionLabel="Pick an activity"
          entriesLabel="Activities"
        />
      );
    case "brain-break-picker":
      return (
        <CardPicker
          storageKey="pickamini:brainbreaks"
          presets={BRAIN_BREAKS}
          actionLabel="Pick a brain break"
          entriesLabel="Brain breaks"
        />
      );
    case "charades-generator":
      return (
        <CardPicker
          storageKey="pickamini:charades"
          presets={CHARADES}
          actionLabel="New charade"
          entriesLabel="Charades words"
          celebrate
        />
      );
    case "pictionary-word-generator":
      return (
        <CardPicker
          storageKey="pickamini:pictionary"
          presets={PICTIONARY}
          actionLabel="New word"
          entriesLabel="Drawing words"
          celebrate
        />
      );
    case "would-you-rather-generator":
      return (
        <CardPicker
          storageKey="pickamini:wyr"
          presets={WOULD_YOU_RATHER}
          actionLabel="New question"
          entriesLabel="Questions"
        />
      );
    case "trivia-question-generator":
      return (
        <CardPicker
          storageKey="pickamini:trivia"
          presets={TRIVIA}
          actionLabel="New trivia"
          entriesLabel="Trivia questions"
        />
      );
    case "random-challenge-generator":
      return (
        <CardPicker
          storageKey="pickamini:challenges"
          presets={CHALLENGES}
          actionLabel="New challenge"
          entriesLabel="Challenges"
          celebrate
        />
      );
    case "spin-the-wheel":
      return (
        <SpinWheel
          storageKey="pickamini:wheel:spin"
          entriesLabel="Entries"
          defaultItems={["Option 1", "Option 2", "Option 3", "Option 4", "Option 5", "Option 6"]}
        />
      );
    case "wheel-of-names":
      return (
        <SpinWheel
          storageKey="pickamini:wheel:names"
          entriesLabel="Names"
          defaultItems={["Alexander", "Sophia", "Ethan", "Olivia", "Lucas", "Ava", "Mason", "Isabella"]}
        />
      );
    case "yes-or-no-wheel":
      return (
        <SpinWheel
          storageKey="pickamini:wheel:yesno"
          entriesLabel="Choices"
          defaultItems={["YES", "NO", "YES", "NO", "YES", "NO"]}
        />
      );
    case "prize-wheel":
    case "lucky-wheel":
      return (
        <SpinWheel
          storageKey="pickamini:wheel:prize"
          entriesLabel="Prizes"
          defaultItems={[
            "Homework Pass 🎫",
            "Sticker Pack ⭐",
            "Line Leader 👑",
            "Choose Music 🎵",
            "5 Bonus Minutes ⏰",
            "Golden Star 🌟",
            "Class Job Swap 🔄",
            "Teacher's Helper 🤝",
          ]}
        />
      );
    case "number-wheel":
      return (
        <SpinWheel
          storageKey="pickamini:wheel:number"
          entriesLabel="Numbers"
          defaultMode="numbers"
        />
      );
    case "food-wheel":
      return (
        <SpinWheel
          storageKey="pickamini:wheel:food"
          entriesLabel="Food Options"
          defaultItems={["Pizza 🍕", "Burger 🍔", "Sushi 🍣", "Tacos 🌮", "Pasta 🍝", "Salad 🥗", "Ramen 🍜", "Fried Chicken 🍗"]}
        />
      );
    case "activity-wheel":
      return (
        <SpinWheel
          storageKey="pickamini:wheel:activity"
          entriesLabel="Activities"
          defaultItems={["Reading Time 📖", "Group Game 🎮", "Math Puzzle 🧩", "Art & Draw 🎨", "Music & Dance 🎵", "Science Quiz 🔬", "Writing Story ✍️", "Brain Break 🧘"]}
        />
      );
    case "student-wheel":
      return (
        <SpinWheel
          storageKey="pickamini:wheel:students"
          entriesLabel="Students"
          defaultItems={["Student 1", "Student 2", "Student 3", "Student 4", "Student 5", "Student 6", "Student 7", "Student 8"]}
        />
      );
    case "decision-wheel":
      return (
        <SpinWheel
          storageKey="pickamini:wheel:decision"
          entriesLabel="Options"
          defaultItems={["Definitely Yes", "No Way", "Maybe Later", "Ask a Friend", "Go For It", "Wait & See"]}
        />
      );
    default:
      return null;
  }
}

import { getToolContent } from "@/lib/tool-content";

function ToolPage() {
  const { tool } = Route.useLoaderData();
  const content = getToolContent(tool.slug, tool.name);

  return (
    <ToolShell tool={tool} content={content}>
      <ToolBody key={tool.slug} slug={tool.slug} />
    </ToolShell>
  );
}
